import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { RESTRICTIONS, BRAZIL_STATES } from "../constants/restrictions";
import { toast } from "sonner";
import { Upload, ImagePlus, MapPinned, Loader2 } from "lucide-react";

export default function SubmitPlace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", address: "", city: "", state: "SP",
    description: "", restrictions: [], photo_path: null,
    website: "", phone: "", latitude: "", longitude: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mapsUrl, setMapsUrl] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const lookupGoogleMaps = async () => {
    if (!mapsUrl.trim()) { toast.error("Cole o link do Google Maps"); return; }
    setLookingUp(true);
    try {
      const { data } = await api.post("/maps/lookup", { url: mapsUrl.trim() });
      setForm((f) => ({
        ...f,
        name: f.name || data.name || "",
        address: data.address || f.address,
        city: data.city || f.city,
        state: data.state || f.state,
        latitude: data.latitude != null ? String(data.latitude) : f.latitude,
        longitude: data.longitude != null ? String(data.longitude) : f.longitude,
      }));
      toast.success("Informações preenchidas! Confira e complete o restante.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLookingUp(false); }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <h1 className="font-heading font-bold text-2xl text-brand-text">Entre para cadastrar um lugar</h1>
        <Button onClick={() => navigate("/login")} className="mt-6 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">Entrar</Button>
      </div>
    );
  }

  const toggleRestriction = (r) => {
    setForm((f) => ({
      ...f,
      restrictions: f.restrictions.includes(r)
        ? f.restrictions.filter((x) => x !== r)
        : [...f.restrictions, r],
    }));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_path: data.path }));
      setPreviewUrl(URL.createObjectURL(file));
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.restrictions.length === 0) { toast.error("Selecione ao menos uma restrição"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.latitude = form.latitude ? parseFloat(form.latitude) : null;
      payload.longitude = form.longitude ? parseFloat(form.longitude) : null;
      await api.post("/places", payload);
      toast.success("Lugar enviado! Aguardando aprovação da moderação.");
      navigate("/perfil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="submit-place-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Cadastrar lugar</span>
      <h1 className="font-heading font-black text-4xl text-brand-text mt-2">Indique um lugar seguro</h1>
      <p className="mt-3 text-brand-textMuted">Compartilhe um restaurante, padaria ou café onde você se sentiu à vontade. Aprovaremos antes de publicar.</p>

      <form onSubmit={submit} className="mt-10 space-y-6 bg-white rounded-3xl border border-brand-border p-6 md:p-10 shadow-sm">
        {/* Google Maps lookup */}
        <div className="rounded-2xl bg-brand-bg border border-brand-border p-5">
          <label className="flex items-center gap-2 text-sm font-bold text-brand-text mb-2">
            <MapPinned className="w-4 h-4 text-brand-primary" /> Importar do Google Maps (opcional)
          </label>
          <p className="text-xs text-brand-textMuted mb-3">
            Cole o link do lugar no Google Maps ("Compartilhar → Copiar link") e preencheremos endereço, cidade, UF e coordenadas automaticamente.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="maps-url-input"
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
              placeholder="https://maps.app.goo.gl/..."
              className="bg-white border-brand-border rounded-2xl h-12 flex-1"
            />
            <Button
              type="button"
              data-testid="maps-lookup-btn"
              onClick={lookupGoogleMaps}
              disabled={lookingUp}
              className="rounded-2xl bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 h-12"
            >
              {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-text mb-2">Nome do lugar *</label>
          <Input data-testid="place-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Café Sem Glúten" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-text mb-2">Endereço completo *</label>
          <Input data-testid="place-address-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rua, número, bairro" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-brand-text mb-2">Cidade *</label>
            <Input data-testid="place-city-input" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-12" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">UF *</label>
            <select data-testid="place-state-select" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-12 px-4 font-bold">
              {BRAZIL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-text mb-2">Restrições atendidas *</label>
          <div className="flex flex-wrap gap-2">
            {RESTRICTIONS.map((r) => {
              const active = form.restrictions.includes(r.value);
              return (
                <button
                  key={r.value} type="button"
                  data-testid={`restriction-toggle-${r.value}`}
                  onClick={() => toggleRestriction(r.value)}
                  className={`px-4 py-2 rounded-full font-bold text-sm border-2 transition-all ${active ? "border-transparent text-white" : "border-brand-border bg-white text-brand-text hover:border-brand-primary"}`}
                  style={active ? { background: r.text, borderColor: r.text } : {}}
                >
                  {r.emoji} {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-text mb-2">Descrição *</label>
          <Textarea data-testid="place-description-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="O que torna este lugar especial para pessoas com restrição alimentar?" className="bg-brand-bg border-brand-border rounded-2xl" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Site (opcional)</label>
            <Input data-testid="place-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Telefone (opcional)</label>
            <Input data-testid="place-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-9999" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Latitude (opcional)</label>
            <Input data-testid="place-lat-input" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="-23.5505" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-2">Longitude (opcional)</label>
            <Input data-testid="place-lng-input" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="-46.6333" className="bg-brand-bg border-brand-border rounded-2xl h-12" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-text mb-2">Foto do lugar</label>
          <label className="flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-brand-border bg-brand-bg p-6 cursor-pointer hover:border-brand-primary transition-colors">
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="w-32 h-32 object-cover rounded-2xl" />
            ) : (
              <>
                <ImagePlus className="w-6 h-6 text-brand-primary" />
                <span className="font-bold text-brand-text">{uploading ? "Enviando..." : "Clique para escolher uma imagem"}</span>
              </>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-input" />
          </label>
        </div>

        <Button data-testid="submit-place-btn" disabled={submitting || uploading} className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold py-6 text-base">
          <Upload className="w-4 h-4 mr-2" /> {submitting ? "Enviando..." : "Enviar para aprovação"}
        </Button>
      </form>
    </div>
  );
}
